/*
  The ranch's own date and time (owner, 2026-10-16: "Always use the ranch's local date/time").

  A date the app fills in ("today" on a form, a default report range) and every time it shows
  are the ranch's, in tenant.Ranches.TimeZone, not the browser's and never UTC: in Arizona the
  UTC date is tomorrow's from 5 p.m., so `new Date().toISOString().slice(0, 10)` dated an
  evening's entries the next day. The active ranch's zone is set by AuthContext when the scope
  changes; until then (or for a zone the browser does not know) the browser's own zone is used.
*/

let zone: string | undefined

/** Set by AuthContext from the active membership's ranch. */
export function setRanchTimeZone(timeZone: string | null | undefined): void {
  zone = timeZone && isKnownZone(timeZone) ? timeZone : undefined
}

export function getRanchTimeZone(): string | undefined {
  return zone
}

function isKnownZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone })
    return true
  } catch {
    return false
  }
}

function parts(at: Date, timeZone: string | undefined): Record<string, string> {
  const out: Record<string, string> = {}
  for (const p of new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(at)) {
    out[p.type] = p.value
  }
  return out
}

/** The ranch's calendar date, `yyyy-mm-dd`. */
export function ranchToday(at: Date = new Date(), timeZone: string | undefined = zone): string {
  const p = parts(at, timeZone)
  return `${p.year}-${p.month}-${p.day}`
}

/** The ranch's calendar year. */
export function ranchYear(at: Date = new Date(), timeZone: string | undefined = zone): number {
  return Number(parts(at, timeZone).year)
}

/** "Oct 15, 2026, 6:19 PM" at the ranch. */
export function formatRanchDateTime(
  iso: string | Date,
  timeZone: string | undefined = zone,
): string {
  return new Date(iso).toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone,
  })
}

/** "Oct 15, 2026" at the ranch. */
export function formatRanchDate(iso: string | Date, timeZone: string | undefined = zone): string {
  return new Date(iso).toLocaleDateString('en-US', { dateStyle: 'medium', timeZone })
}

/** The zone's offset from UTC at an instant, in milliseconds (Phoenix: -7 h). */
function offsetMs(at: Date, timeZone: string | undefined): number {
  const p: Record<string, string> = {}
  for (const x of new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at)) {
    p[x.type] = x.value
  }
  const wall = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour),
    Number(p.minute),
    Number(p.second),
  )
  return wall - Math.floor(at.getTime() / 1000) * 1000
}

/**
 * A time typed at the ranch (`yyyy-mm-ddThh:mm`, as a datetime-local input gives it) as the
 * instant it names. The offset is taken twice so a time near a daylight-saving change lands on
 * the zone's own rule, not the browser's.
 */
export function ranchLocalToUtc(local: string, timeZone: string | undefined = zone): Date {
  const [d = '', t = '00:00'] = local.split('T')
  const [y, mo, da] = d.split('-').map(Number)
  const [h, mi] = t.split(':').map(Number)
  const guess = Date.UTC(y ?? 1970, (mo ?? 1) - 1, da ?? 1, h ?? 0, mi ?? 0)
  const first = guess - offsetMs(new Date(guess), timeZone)
  return new Date(guess - offsetMs(new Date(first), timeZone))
}
